import React from "react";
import { useState, useEffect } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import { useMutation, useQuery } from "@tanstack/react-query";
import api from "./Api/api";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import OrderSummary from "./OrderSummary";
import { toast } from "react-toastify";

const Details = () => {
  const cartreduxdata = useSelector((state) => state.cart);
  const packagesreduxdata = useSelector((state) => state.packages);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  //  Authentication check
  useEffect(() => {
    const userFromStorage = localStorage.getItem("user");
    const token = localStorage.getItem("token");

    if (!userFromStorage || !token) {
      console.log("⚠️ No authentication found, redirecting to login");
      dispatch({ type: "CHECKOUT_STATUS", payload: 1 });
      navigate("/login");
      return;
    }

    try {
      const parsedUser = JSON.parse(userFromStorage);
      setUser(parsedUser);
      console.log("User authenticated:", parsedUser);
    } catch (e) {
      console.error("Error parsing user data:", e);
      navigate("/login");
    }
  }, [navigate, dispatch]);

  const [deliveryMethod, setDeliveryMethod] = useState("delivery");
  const [name, setName] = useState("");
  const [lastname, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [address2, setAddress2] = useState("");
  const [mobile, setMobile] = useState("");
  const [collectDate, setCollectDate] = useState("");
  const [city, setCity] = useState("");
  const [county, setCounty] = useState("");
  const [postalcode, setPostalcode] = useState("");
  const [country, setCountry] = useState("GB");
  const [user, setUser] = useState({});
  const [selectedTimeSlot, setSelectedTimeSlot] = useState("");

  // for collection date validation start
  // 👉 Today's date (YYYY-MM-DD)
  const today = new Date().toISOString().split("T")[0];

  // 👉 Check weekend
  const isWeekend = (selectedCollectionDate) => {
    const day = new Date(selectedCollectionDate).getDay();
    return day === 0 || day === 6; // Sunday = 0, Saturday = 6
  };

  const handleDateChange = (e) => {
    const selectedCollectionDate = e.target.value;

    if (isWeekend(selectedCollectionDate)) {
      alert("Saturday & Sunday are not allowed");
      return;
    }

    setCollectDate(selectedCollectionDate);
  };

  // collection date validation end

  // Shipping rates state
  const [isCalculatingShipping, setIsCalculatingShipping] = useState(false);
  const [shippingRates, setShippingRates] = useState([]);
  const [selectedShippingRate, setSelectedShippingRate] = useState(null);
  const [shippingError, setShippingError] = useState(null);
  const [showShippingOptions, setShowShippingOptions] = useState(false);

  const [serviceType, setServiceType] = useState(null);

  // ============ PALLET ZONE STATES - SIMPLIFIED (Only 2 Dropdowns) ============
  const [selectedZone, setSelectedZone] = useState("");
  const [selectedPalletDeliveryType, setSelectedPalletDeliveryType] =
    useState("");
  const [palletPrice, setPalletPrice] = useState(null);

  // API Data States
  const [zonesData, setZonesData] = useState([]);
  const [deliveryTypesData, setDeliveryTypesData] = useState([]); // Now includes price

  // Loading States
  const [loadingZones, setLoadingZones] = useState(false);
  const [loadingDeliveryTypes, setLoadingDeliveryTypes] = useState(false);
  // ============ PALLET ZONE STATES - END ============

  // ✅ Get service type from localStorage
  useEffect(() => {
    const storedServiceType = localStorage.getItem("service_type");
    setServiceType(Number(storedServiceType));
    console.log("📦 Service Type loaded:", storedServiceType);
  }, []);

  //  ✅ Fetch deliveryChargesData from API
  const { data: deliveryChargesData, isLoading: loadingDeliveryCharges } =
    useQuery(["deliveryCharges"], () => api.get("/users/getdeliverycharges"), {
      refetchOnWindowFocus: false,
      retry: 1,
      select: (res) => res.data,
    });

  //  Fetch time slots from API
  const { data: timeSlotsData, isLoading: loadingTimeSlots } = useQuery(
    ["collectionTimeSlots"],
    () => api.get("/collection/time-slots"),
    {
      refetchOnWindowFocus: false,
      retry: 1,
      select: (res) => res.data,
    }
  );

  const timeSlots = timeSlotsData?.data || [];

  useEffect(() => {
    if (localStorage.getItem("user")) {
      setUser(JSON.parse(localStorage.getItem("user")));
    }
  }, []);

  // ✅ Get delivery charges
  const courierCharge =
    deliveryChargesData?.data?.find((item) => item.id === 1)?.delivery_charge ||
    0;
  const palletCharge =
    deliveryChargesData?.data?.find((item) => item.id === 2)?.delivery_charge ||
    0;

  console.log("📦 Delivery Charges:", {
    courierCharge,
    palletCharge,
    serviceType,
  });

  // ✅ FIXED: Set delivery as default for serviceType === 0
  useEffect(() => {
    localStorage.setItem("order_type", "delivery");

    // ✅ For serviceType === 0, don't auto-apply charge - user will select from dropdowns
    if (serviceType === 0) {
      console.log(
        "✅ Pallet delivery mode - user will select zone and pricing"
      );
    }
  }, [serviceType, dispatch]);

  var total = 0;
  cartreduxdata.forEach((item) => {
    total += item.cartamount;
  });
  var packtotal = 0;

  packagesreduxdata.forEach((item) => {
    if (item && item.cartamount) {
      packtotal += item.cartamount;
    }
  });

  var gtotal = parseFloat(total) + parseFloat(packtotal);

  // ============ PALLET ZONE API HANDLERS - START ============

  // Fetch Zones on Component Mount (for serviceType === 0)
  useEffect(() => {
    if (serviceType === 0) {
      fetchZones();
    }
  }, [serviceType]);

  // Fetch All Zones (Dropdown 1)
  const fetchZones = async () => {
    setLoadingZones(true);
    try {
      const response = await api.get("/users/shipping-zones/zones");
      if (response.data.status) {
        setZonesData(response.data.data);
        console.log("✅ Zones fetched:", response.data.data);
      }
    } catch (error) {
      console.error("❌ Error fetching zones:", error);
      setZonesData([]);
    } finally {
      setLoadingZones(false);
    }
  };

  // Fetch Delivery Types WITH PRICES when Zone is selected (Dropdown 2)
  const fetchDeliveryTypes = async (zoneCode) => {
    setLoadingDeliveryTypes(true);
    setDeliveryTypesData([]);
    try {
      const response = await api.get(
        `/users/shipping-zones/delivery-types?zone_code=${zoneCode}`
      );
      if (response.data.status) {
        setDeliveryTypesData(response.data.data);
        console.log(
          "✅ Delivery types with prices fetched:",
          response.data.data
        );
      }
    } catch (error) {
      console.error("❌ Error fetching delivery types:", error);
      setDeliveryTypesData([]);
    } finally {
      setLoadingDeliveryTypes(false);
    }
  };

  // Handle Zone Change (Dropdown 1)
  const handleZoneChange = (e) => {
    const zoneCode = e.target.value;
    setSelectedZone(zoneCode);
    setSelectedPalletDeliveryType("");
    setPalletPrice(null);
    setDeliveryTypesData([]);

    if (zoneCode) {
      fetchDeliveryTypes(zoneCode);
    }

    // Clear shipping data
    setSelectedShippingRate(null);
    localStorage.removeItem("selectedShipping");
  };

  // Handle Delivery Type Change (Dropdown 2) - NOW DIRECTLY SETS PRICE
  const handlePalletDeliveryTypeChange = (e) => {
    const deliveryType = e.target.value;
    setSelectedPalletDeliveryType(deliveryType);

    if (deliveryType && deliveryTypesData.length > 0) {
      const selectedOption = deliveryTypesData.find(
        (item) => item.value === deliveryType
      );
      const price = selectedOption?.price
        ? parseFloat(selectedOption.price)
        : null;
      setPalletPrice(price);

      if (price) {
        // Get zone name for display
        const zoneName =
          zonesData.find((z) => z.zone_code === selectedZone)?.zone_name ||
          selectedZone;
        const deliveryLabel = selectedOption?.label || deliveryType;

        const palletShippingData = {
          delivery: 999,
          rate: price,
          carrier: "Pallet Delivery",
          service: `${zoneName} - ${deliveryLabel}`,
          delivery_days:
            deliveryType === "next_day"
              ? "1 business day"
              : "3-5 business days",
          shipment_id: "PALLET",
          rate_id: `PALLET_${selectedZone}_${deliveryType}`,
          zone: selectedZone,
          deliveryType: deliveryType,
        };

        setSelectedShippingRate(palletShippingData);
        dispatch({ type: "ADD_SERVICES", payload: palletShippingData });
        localStorage.setItem(
          "selectedShipping",
          JSON.stringify(palletShippingData)
        );

        console.log("✅ Pallet shipping selected:", palletShippingData);
      }
    } else {
      setPalletPrice(null);
      setSelectedShippingRate(null);
    }
  };

  // Reset Pallet Zone Selection
  const resetPalletZoneSelection = () => {
    setSelectedZone("");
    setSelectedPalletDeliveryType("");
    setPalletPrice(null);
    setDeliveryTypesData([]);
  };

  // ============ PALLET ZONE API HANDLERS - END ============

  // ✅ UPDATED: Calculate shipping with Courier Packaging charge
  const handleCalculateShipping = () => {
    if (!address || !city || !postalcode) {
      setShippingError("Please enter complete address to calculate shipping");
      return;
    }

    setIsCalculatingShipping(true);
    setShippingError(null);
    setShowShippingOptions(false);

    const totalWeight = cartreduxdata.length * 1;

    const shippingBody = {
      toAddress: {
        name: `${name || "Customer"} ${lastname || ""}`,
        street1: address,
        street2: address2 || "",
        city: city,
        state: county || "",
        zip: postalcode,
        country: country || "GB",
        phone: mobile,
      },
      parcel: {
        length: "12",
        width: "10",
        height: "6",
        weight: String(Math.max(totalWeight, 1)),
        distance_unit: "in",
        mass_unit: "lb",
      },
    };

    mutationCalculateShipping.mutate(shippingBody);
  };

  const mutationCalculateShipping = useMutation(
    (body) => {
      return api.post("shipping/calculate-shipping", body);
    },
    {
      onError: (error) => {
        console.error("Shipping calculation error:", error);
        setShippingError(
          "Failed to calculate shipping rates. Please try again."
        );
        setIsCalculatingShipping(false);
        setShippingRates([]);
        setShowShippingOptions(false);
      },
      onSuccess: (response) => {
        console.log("Shipping calculated:", response.data);

        if (response.data.status === "success" && response.data.data.rates) {
          let rates = response.data.data.rates;

          // ✅ Add Courier Packaging charge (£2) to all rates if serviceType === 1
          if (serviceType === 1 && courierCharge > 0) {
            rates = rates.map((rate) => ({
              ...rate,
              originalRate: rate.rate,
              rate: (parseFloat(rate.rate) + parseFloat(courierCharge)).toFixed(
                2
              ),
              courierCharge: courierCharge,
            }));
            console.log(
              "✅ Added Courier Packaging charge to all rates:",
              courierCharge
            );
          }

          // ✅ NEW: Filter out "ParcelShop Drop-Off" (first option)
          rates = rates.filter(
            (rate) => rate.service !== "ParcelShop Drop-Off"
          );
          console.log(`✅ Filtered rates, showing ${rates.length} options`);

          if (rates.length > 0) {
            setShippingRates(rates);
            setShowShippingOptions(true);
            setShippingError(null);
            setSelectedShippingRate(null);
            console.log(`✅ Found ${rates.length} shipping options`);
          } else {
            setShippingError("No shipping rates available for this address");
            setShippingRates([]);
            setShowShippingOptions(false);
          }
        } else {
          setShippingError("No shipping rates available");
          setShippingRates([]);
          setShowShippingOptions(false);
        }

        setIsCalculatingShipping(false);
      },
    }
  );

  const handleSelectShippingRate = (rate) => {
    setSelectedShippingRate(rate);

    const shippingData = {
      delivery: 999,
      rate: rate.rate,
      originalRate: rate.originalRate || rate.rate,
      courierCharge: rate.courierCharge || 0,
      carrier: rate.carrier,
      service: rate.service,
      delivery_days: rate.delivery_days,
      shipment_id: rate.shipment_id,
      rate_id: rate.id,
    };

    dispatch({ type: "ADD_SERVICES", payload: shippingData });
    localStorage.setItem("selectedShipping", JSON.stringify(shippingData));

    console.log("Selected shipping:", shippingData);
  };

  // ✅ UPDATE: Delivery mutation - Store delivery_id in localStorage
  const mutationSubmit = useMutation(
    (body) => {
      const headers = {
        headers: {
          "content-type": "application/json",
        },
      };
      return api.post("users/addcustomers", body, headers);
    },
    {
      onError: (error, variables, context) => {
        console.log(error);
        toast.error("Failed to save customer details", {
          position: toast.POSITION.TOP_CENTER,
        });
      },
      onSuccess: (data, variables, context) => {
        if (data?.data?.insertId) {
          // ✅ CRITICAL: Clear collection data first
          localStorage.removeItem("collection_id");

          // ✅ Then set delivery data
          localStorage.setItem("customerId", data.data.insertId);
          localStorage.setItem("delivery_id", data.data.insertId);
          localStorage.setItem("order_type", "delivery");

          console.log("✅ Delivery ID saved:", data.data.insertId);
          console.log("✅ Order type set to: delivery");
        }
        toast.success("Proceeding to checkout", {
          position: toast.POSITION.TOP_CENTER,
        });
        navigate("/checkout");
      },
    }
  );

  // ✅ UPDATE: Collection Request mutation
  const collectionRequest = useMutation(
    (body) => {
      return api.post("/collection/request", body);
    },
    {
      onError: (error) => {
        console.error("Collection request error:", error);
        toast.error("Failed to submit collection request. Please try again.", {
          position: toast.POSITION.TOP_CENTER,
        });
      },
      onSuccess: (response) => {
        console.log("Collection request submitted:", response.data);
        toast.success("Collection request submitted successfully!", {
          position: toast.POSITION.TOP_CENTER,
        });

        // ✅ CRITICAL: Clear delivery data first
        localStorage.removeItem("delivery_id");
        localStorage.removeItem("customerId");
        localStorage.removeItem("selectedShipping");

        // ✅ Then set collection data
        if (response.data?.data?.request_id) {
          localStorage.setItem("collection_id", response.data.data.request_id);
          localStorage.setItem("order_type", "collection");

          console.log("✅ Collection ID saved:", response.data.data.request_id);
          console.log("✅ Order type set to: collection");
        }

        // Navigate to checkout
        navigate("/checkout");
      },
    }
  );

  const handleSubmit = () => {
    // ✅ DELIVERY
    if (deliveryMethod === "delivery") {
      // ✅ Validation for shipping rate
      if (!selectedShippingRate) {
        if (serviceType === 1) {
          toast.error("Please select a shipping option", {
            position: toast.POSITION.TOP_CENTER,
          });
          return;
        }
        if (serviceType === 0) {
          toast.error("Please select Zone and Delivery Type", {
            position: toast.POSITION.TOP_CENTER,
          });
          return;
        }
      }

      var deliveryBody = {
        name,
        lastname,
        city,
        mobile,
        address: address,
        address2: address2,
        county: county,
        postalcode: postalcode,
        country: country,
        userid: user?.id,
        delivery_method: deliveryMethod,
      };

      console.log("Delivery customer data:", deliveryBody);
      mutationSubmit.mutate(deliveryBody);
    }
    // ✅ COLLECTION
    else {
      // Validation
      if (!name || !lastname || !mobile) {
        toast.error("Please fill in all required fields", {
          position: toast.POSITION.TOP_CENTER,
        });
        return;
      }

      // ✅ ADD: Email validation for collection (mandatory)
      if (!email || email.trim() === "") {
        toast.error("Email is required for collection orders", {
          position: toast.POSITION.TOP_CENTER,
        });
        return;
      }

      // Email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        toast.error("Please enter a valid email address", {
          position: toast.POSITION.TOP_CENTER,
        });
        return;
      }

      // ✅ CHANGED: Single time slot validation
      if (!selectedTimeSlot) {
        toast.error("Please select a time slot", {
          position: toast.POSITION.TOP_CENTER,
        });
        return;
      }

      var collectionBody = {
        user_id: user?.id || null,
        name: name,
        last_name: lastname,
        email: email,
        mobile: mobile,
        collection_time_slots: selectedTimeSlot,
        collectDate: collectDate,
      };

      console.log("Collection request data:", collectionBody);
      collectionRequest.mutate(collectionBody);
    }
  };

  return (
    <div className="checkoutPageSec">
      <div className="container">
        <div className="row">
          {/* LEFT SIDE */}
          <div className="col-md-7">
            {/* ✅ Delivery/Collection Selection - Show for BOTH serviceType === 0 and serviceType === 1 */}
            {(serviceType === 1 || serviceType === 0) && (
              <div
                style={{
                  marginBottom: "25px",
                  padding: "20px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "8px",
                  border: "1px solid #dee2e6",
                }}
              >
                <h4
                  style={{
                    marginBottom: "15px",
                    fontWeight: "600",
                    color: "#333",
                  }}
                >
                  Select Delivery Method
                </h4>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "15px",
                  }}
                >
                  <div
                    onClick={() => {
                      setDeliveryMethod("delivery");
                      setShippingRates([]);
                      setSelectedShippingRate(null);
                      setShowShippingOptions(false);
                      setSelectedTimeSlot("");

                      localStorage.removeItem("collection_id");
                      localStorage.setItem("order_type", "delivery");
                      console.log(
                        "🔄 Switched to delivery, set order_type to 'delivery'"
                      );

                      // Reset pallet zone selection when switching to delivery
                      resetPalletZoneSelection();
                    }}
                    style={{
                      padding: "15px 20px",
                      border:
                        deliveryMethod === "delivery"
                          ? "3px solid #28a745"
                          : "2px solid #ddd",
                      borderRadius: "8px",
                      cursor: "pointer",
                      backgroundColor:
                        deliveryMethod === "delivery" ? "#e8f5e9" : "white",
                      transition: "all 0.3s",
                      display: "flex",
                      alignItems: "center",
                      gap: "15px",
                    }}
                  >
                    <i
                      className="fa-solid fa-truck"
                      style={{
                        fontSize: "28px",
                        color:
                          deliveryMethod === "delivery" ? "#28a745" : "#666",
                      }}
                    ></i>
                    <div>
                      <h5
                        style={{
                          margin: 0,
                          color:
                            deliveryMethod === "delivery" ? "#28a745" : "#333",
                          fontWeight: "600",
                        }}
                      >
                        Delivery
                      </h5>
                      <p
                        style={{
                          fontSize: "14px",
                          color: "#666",
                          margin: 0,
                          marginTop: "3px",
                        }}
                      >
                        Get it delivered to your address
                      </p>
                    </div>
                  </div>

                  <div
                    onClick={() => {
                      setDeliveryMethod("collection");
                      setShippingRates([]);
                      setSelectedShippingRate(null);
                      setShowShippingOptions(false);

                      localStorage.removeItem("delivery_id");
                      localStorage.removeItem("customerId");
                      localStorage.removeItem("selectedShipping");
                      localStorage.removeItem("easypostShipping");
                      localStorage.setItem("order_type", "collection");
                      console.log(
                        "🔄 Switched to collection, set order_type to 'collection'"
                      );

                      // Reset pallet zone selection
                      resetPalletZoneSelection();

                      // ✅ IMPORTANT: Set shipping rate to null/empty for collection
                      const emptyShipping = {
                        delivery: 0,
                        rate: 0,
                        carrier: null,
                        service: null,
                        delivery_days: null,
                        shipment_id: null,
                        rate_id: null,
                      };
                      dispatch({
                        type: "ADD_SERVICES",
                        payload: emptyShipping,
                      });
                    }}
                    style={{
                      padding: "15px 20px",
                      border:
                        deliveryMethod === "collection"
                          ? "3px solid #28a745"
                          : "2px solid #ddd",
                      borderRadius: "8px",
                      cursor: "pointer",
                      backgroundColor:
                        deliveryMethod === "collection" ? "#e8f5e9" : "white",
                      transition: "all 0.3s",
                      display: "flex",
                      alignItems: "center",
                      gap: "15px",
                    }}
                  >
                    <i
                      className="fa-solid fa-store"
                      style={{
                        fontSize: "28px",
                        color:
                          deliveryMethod === "collection" ? "#28a745" : "#666",
                      }}
                    ></i>
                    <div>
                      <h5
                        style={{
                          margin: 0,
                          color:
                            deliveryMethod === "collection"
                              ? "#28a745"
                              : "#333",
                          fontWeight: "600",
                        }}
                      >
                        Collection
                      </h5>
                      <p
                        style={{
                          fontSize: "14px",
                          color: "#666",
                          margin: 0,
                          marginTop: "3px",
                        }}
                      >
                        Pick up from our location
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ✅ SIMPLIFIED Zone Dropdowns for serviceType === 0 (Pallet Delivery) - ONLY 2 DROPDOWNS */}
            {serviceType === 0 && deliveryMethod === "delivery" && (
              <div
                style={{
                  marginBottom: "25px",
                  padding: "20px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "8px",
                  border: "1px solid #dee2e6",
                }}
              >
                <h5
                  style={{
                    marginBottom: "15px",
                    fontWeight: "600",
                    color: "#333",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <i
                    className="fa-solid fa-truck"
                    style={{ color: "#28a745" }}
                  ></i>
                  Pallet Delivery Options
                </h5>

                {/* Dropdown 1: Zone Selection */}
                <Form.Group className="mb-3">
                  <Form.Label>
                    Select Zone <span style={{ color: "red" }}>*</span>
                  </Form.Label>
                  <Form.Select
                    value={selectedZone}
                    onChange={handleZoneChange}
                    disabled={loadingZones}
                  >
                    <option value="">
                      {loadingZones ? "Loading zones..." : "-- Select Zone --"}
                    </option>
                    {zonesData.map((zone) => (
                      <option key={zone.zone_code} value={zone.zone_code}>
                        {zone.zone_name}{" "}
                        {zone.postcodes ? `(${zone.postcodes})` : ""}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                {/* Dropdown 2: Delivery Type WITH PRICE - THIS IS NOW THE FINAL SELECTION */}
                {selectedZone && (
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Delivery Type <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <Form.Select
                      value={selectedPalletDeliveryType}
                      onChange={handlePalletDeliveryTypeChange}
                      disabled={loadingDeliveryTypes}
                    >
                      <option value="">
                        {loadingDeliveryTypes
                          ? "Loading delivery types..."
                          : "-- Select Delivery Type --"}
                      </option>
                      {deliveryTypesData.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label} - £
                          {parseFloat(type.price || 0).toFixed(2)}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                )}

                {/* Price Display */}
                {palletPrice !== null && (
                  <div
                    style={{
                      marginTop: "15px",
                      padding: "15px",
                      backgroundColor: "#e8f5e9",
                      borderRadius: "8px",
                      border: "2px solid #28a745",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <strong style={{ color: "#28a745" }}>
                        Delivery Charge
                      </strong>
                      <p style={{ margin: 0, fontSize: "13px", color: "#666" }}>
                        {
                          zonesData.find((z) => z.zone_code === selectedZone)
                            ?.zone_name
                        }{" "}
                        |{" "}
                        {
                          deliveryTypesData.find(
                            (d) => d.value === selectedPalletDeliveryType
                          )?.label
                        }
                      </p>
                    </div>
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "#28a745",
                      }}
                    >
                      £{parseFloat(palletPrice || 0).toFixed(2)}
                    </div>
                  </div>
                )}

                {/* Warning if not all selected */}
                {!palletPrice && (
                  <div
                    style={{
                      marginTop: "10px",
                      padding: "10px",
                      backgroundColor: "#fff3cd",
                      border: "1px solid #ffc107",
                      borderRadius: "5px",
                      color: "#856404",
                      fontSize: "13px",
                    }}
                  >
                    <i
                      className="fa-solid fa-info-circle"
                      style={{ marginRight: "5px" }}
                    ></i>
                    Please select Zone and Delivery Type to see the delivery
                    charge
                  </div>
                )}
              </div>
            )}

            {/* Form Section */}
            <div
              style={{
                padding: "25px",
                backgroundColor: "white",
                borderRadius: "8px",
                border: "1px solid #dee2e6",
              }}
            >
              <h4
                style={{
                  marginBottom: "20px",
                  fontWeight: "600",
                  color: "#333",
                  textAlign: "center",
                }}
              >
                {deliveryMethod === "delivery"
                  ? "Shipping Details"
                  : "Contact Details"}
              </h4>

              {shippingError && (
                <div
                  style={{
                    background: "#fff3cd",
                    border: "1px solid #ffc107",
                    padding: "10px",
                    borderRadius: "5px",
                    marginBottom: "15px",
                    color: "#856404",
                  }}
                >
                  {shippingError}
                </div>
              )}

              <Form>
                <div className="row">
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label>
                        First Name <span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="First name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                      />
                    </Form.Group>
                  </div>

                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Last Name <span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Last Name"
                        value={lastname}
                        onChange={(e) => setLastName(e.target.value)}
                      />
                    </Form.Group>
                  </div>
                </div>

                {/* ✅ EMAIL FIELD - MANDATORY for Collection */}
                {deliveryMethod === "collection" && (
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Email <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="email@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </Form.Group>
                )}

                <Form.Group className="mb-3">
                  <Form.Label>
                    Mobile <span style={{ color: "red" }}>*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Mobile (e.g., 07123456789)"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                  />
                </Form.Group>

                {deliveryMethod === "collection" &&
                  (serviceType === 1 || serviceType === 0) && (
                <Form.Group className="mb-3">
                  <Form.Label>Collection Date <span style={{ color: "red" }}>*</span></Form.Label>
                  <Form.Control 
                    type="date"
                    placeholder="Select Collection Date"
                    value={collectDate}
                    min={today}
                    // onChange={(e)=>setCollectDate(e.target.value)}
                    onChange={handleDateChange}
                  />
                </Form.Group>
                  )}

                {/* ✅ Collection Time Slots - Show for BOTH serviceType === 0 and serviceType === 1 */}
                {deliveryMethod === "collection" &&
                  (serviceType === 1 || serviceType === 0) && (
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Available Collection Time Slot (Collection is only possible during office hours [Mon-Fri]){" "}
                        <span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <Form.Text
                        className="d-block mb-2"
                        style={{ color: "#666" }}
                      >
                        <i
                          className="fa-solid fa-clock"
                          style={{ marginRight: "5px" }}
                        ></i>
                        Select one time slot
                      </Form.Text>

                      {loadingTimeSlots ? (
                        <div style={{ textAlign: "center", padding: "20px" }}>
                          <i
                            className="fa-solid fa-spinner fa-spin"
                            style={{ fontSize: "24px", color: "#28a745" }}
                          ></i>
                          <p style={{ marginTop: "10px", color: "#666" }}>
                            Loading time slots...
                          </p>
                        </div>
                      ) : (
                        <>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(2, 1fr)",
                              gap: "10px",
                              marginTop: "10px",
                            }}
                          >
                            {timeSlots.map((slot) => (
                              <div
                                key={slot.id}
                                onClick={() =>
                                  setSelectedTimeSlot(slot.slot_value)
                                }
                                style={{
                                  padding: "12px 15px",
                                  border:
                                    selectedTimeSlot === slot.slot_value
                                      ? "3px solid #28a745"
                                      : "2px solid #ddd",
                                  borderRadius: "8px",
                                  cursor: "pointer",
                                  backgroundColor:
                                    selectedTimeSlot === slot.slot_value
                                      ? "#e8f5e9"
                                      : "white",
                                  transition: "all 0.3s",
                                  textAlign: "center",
                                  fontWeight:
                                    selectedTimeSlot === slot.slot_value
                                      ? "600"
                                      : "normal",
                                  color:
                                    selectedTimeSlot === slot.slot_value
                                      ? "#28a745"
                                      : "#333",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: "8px",
                                }}
                              >
                                {selectedTimeSlot === slot.slot_value && (
                                  <i
                                    className="fa-solid fa-check-circle"
                                    style={{ color: "#28a745" }}
                                  ></i>
                                )}
                                <span style={{ fontSize: "14px" }}>
                                  {slot.slot_label}
                                </span>
                              </div>
                            ))}
                          </div>

                          {selectedTimeSlot && (
                            <div
                              style={{
                                marginTop: "15px",
                                padding: "10px",
                                backgroundColor: "#e8f5e9",
                                border: "1px solid #28a745",
                                borderRadius: "5px",
                                color: "#155724",
                                fontSize: "14px",
                              }}
                            >
                              <strong>Selected slot:</strong>{" "}
                              {
                                timeSlots.find(
                                  (s) => s.slot_value === selectedTimeSlot
                                )?.slot_label
                              }
                            </div>
                          )}

                          {!selectedTimeSlot && (
                            <div
                              style={{
                                marginTop: "15px",
                                padding: "10px",
                                backgroundColor: "#fff3cd",
                                border: "1px solid #ffc107",
                                borderRadius: "5px",
                                color: "#856404",
                                fontSize: "14px",
                              }}
                            >
                              <i
                                className="fa-solid fa-info-circle"
                                style={{ marginRight: "5px" }}
                              ></i>
                              Please select a time slot
                            </div>
                          )}
                        </>
                      )}
                    </Form.Group>
                  )}

                {/* ✅ Delivery Address Fields - Always show for delivery */}
                {deliveryMethod === "delivery" && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Address Line 1 <span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="House number and street name"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Address Line 2 (Optional)</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Flat, apartment, building (optional)"
                        value={address2}
                        onChange={(e) => setAddress2(e.target.value)}
                      />
                    </Form.Group>

                    <div className="row">
                      <div className="col-md-6">
                        <Form.Group className="mb-3">
                          <Form.Label>
                            City <span style={{ color: "red" }}>*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="City (e.g., London)"
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                            required
                          />
                        </Form.Group>
                      </div>

                      <div className="col-md-6">
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Post Code <span style={{ color: "red" }}>*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="e.g., SW1A 1AA"
                            value={postalcode}
                            onChange={(e) => setPostalcode(e.target.value)}
                            required
                          />
                        </Form.Group>
                      </div>
                    </div>

                    <Form.Group className="mb-3">
                      <Form.Label>Country</Form.Label>
                      <Form.Select
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                      >
                        <option value="GB">United Kingdom</option>
                      </Form.Select>
                    </Form.Group>

                    {/* ✅ Calculate Shipping Button - ONLY for serviceType === 1 */}
                    {serviceType === 1 && (
                      <Button
                        variant="primary"
                        className="btn2"
                        onClick={handleCalculateShipping}
                        disabled={
                          isCalculatingShipping ||
                          !address ||
                          !city ||
                          !postalcode
                        }
                        style={{ marginBottom: "20px", width: "100%" }}
                      >
                        {isCalculatingShipping ? (
                          <>
                            <i
                              className="fa-solid fa-spinner fa-spin"
                              style={{ marginRight: "8px" }}
                            ></i>
                            Calculating Shipping Options...
                          </>
                        ) : (
                          <>
                            <i
                              className="fa-solid fa-calculator"
                              style={{ marginRight: "8px" }}
                            ></i>
                            Calculate Shipping Options
                          </>
                        )}
                      </Button>
                    )}

                    {/* ✅ Shipping Options Display - ONLY for serviceType === 1 */}

                    {serviceType === 1 &&
                      showShippingOptions &&
                      shippingRates.length > 0 && (
                        <div
                          style={{
                            marginBottom: "20px",
                            padding: "0",
                            backgroundColor: "white",
                            borderRadius: "0",
                            border: "none",
                          }}
                        >
                          {shippingRates.map((rate, index) => (
                            <div
                              key={index}
                              onClick={() => handleSelectShippingRate(rate)}
                              style={{
                                padding: "15px 0",
                                marginBottom: "0",
                                borderBottom:
                                  index < shippingRates.length - 1
                                    ? "1px solid #eee"
                                    : "none",
                                cursor: "pointer",
                                backgroundColor: "white",
                                transition: "background-color 0.2s",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                              onMouseEnter={(e) => {
                                if (selectedShippingRate?.id !== rate.id) {
                                  e.currentTarget.style.backgroundColor =
                                    "#f8f9fa";
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (selectedShippingRate?.id !== rate.id) {
                                  e.currentTarget.style.backgroundColor =
                                    "white";
                                }
                              }}
                            >
                              <div style={{ flex: 1 }}>
                                <div
                                  style={{
                                    fontSize: "15px",
                                    color: "#333",
                                    fontWeight:
                                      selectedShippingRate?.id === rate.id
                                        ? "600"
                                        : "normal",
                                  }}
                                >
                                  {selectedShippingRate?.id === rate.id && (
                                    <i
                                      className="fa-solid fa-check-circle"
                                      style={{
                                        marginRight: "8px",
                                        color: "#28a745",
                                      }}
                                    ></i>
                                  )}

                                  {rate.service
                                    .replace(
                                      "ParcelShop Drop-Off Next Day",
                                      "Standard 2-4 Day"
                                    )
                                    .replace(
                                      "ParcelShop Drop-Off",
                                      "Standard 2-4 Day"
                                    )
                                    .replace("Next Day", "Next Working Day")
                                    .replace("Next day", "Next Working Day")}
                                </div>
                                <div
                                  style={{
                                    fontSize: "13px",
                                    color: "#999",
                                    marginTop: "2px",
                                  }}
                                >
                                  {rate.delivery_days !== "N/A"
                                    ? `Delivery: ${rate.delivery_days}`
                                    : ""}
                                </div>
                              </div>
                              <div
                                style={{
                                  fontSize: "16px",
                                  fontWeight: "600",
                                  color:
                                    selectedShippingRate?.id === rate.id
                                      ? "#28a745"
                                      : "#333",
                                }}
                              >
                                £{parseFloat(rate.rate).toFixed(2)}
                              </div>
                            </div>
                          ))}

                          {!selectedShippingRate && (
                            <div
                              style={{
                                padding: "10px",
                                backgroundColor: "#fff3cd",
                                border: "1px solid #ffc107",
                                borderRadius: "5px",
                                color: "#856404",
                                fontSize: "13px",
                                marginTop: "15px",
                              }}
                            >
                              <i
                                className="fa-solid fa-info-circle"
                                style={{ marginRight: "5px" }}
                              ></i>
                              Please select a shipping option to continue
                            </div>
                          )}
                        </div>
                      )}
                  </>
                )}

                <Button
                  variant="success"
                  className="btn2"
                  onClick={handleSubmit}
                  disabled={
                    deliveryMethod === "delivery"
                      ? ((serviceType === 1 || serviceType === 0) &&
                          !selectedShippingRate) ||
                        isCalculatingShipping ||
                        mutationSubmit.isLoading
                      : !selectedTimeSlot || collectionRequest.isLoading
                  }
                  style={{ width: "100%", marginTop: "15px" }}
                >
                  {mutationSubmit.isLoading || collectionRequest.isLoading ? (
                    <>
                      <i
                        className="fa-solid fa-spinner fa-spin"
                        style={{ marginRight: "8px" }}
                      ></i>
                      Processing...
                    </>
                  ) : deliveryMethod === "delivery" &&
                    (serviceType === 1 || serviceType === 0) &&
                    !selectedShippingRate ? (
                    serviceType === 0 ? (
                      "Select Delivery Options"
                    ) : (
                      "Select Shipping Option First"
                    )
                  ) : deliveryMethod === "collection" && !selectedTimeSlot ? (
                    "Select Time Slot"
                  ) : deliveryMethod === "delivery" ? (
                    "Proceed to Checkout"
                  ) : (
                    "Submit Collection Request"
                  )}
                </Button>
              </Form>
            </div>
          </div>

          {/* RIGHT SIDE */}
          <div className="col-md-5">
            <div style={{ position: "sticky", top: "20px" }}>
              {deliveryMethod === "delivery" ? (
                <OrderSummary selectedShippingRate={selectedShippingRate} />
              ) : (
                <OrderSummary selectedShippingRate={null} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Details;
